import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSession } from 'next-auth/react'
import type { BulkGetItem } from '@ttab/elephant-api/repository'
import { useRegistry } from '@/hooks/useRegistry'
import { useRepositoryEvents } from '@/hooks/useRepositoryEvents'

export interface VersionEntry {
  version: bigint
  created: string
  status: string
}

/**
 * The latest page of document versions with their first status, plus the
 * documents for each version, refreshed on repository events.
 */
export const useVersionHistory = ({ uuid, documentType, withStatusOnly, enabled = true }: {
  uuid: string
  documentType?: string
  withStatusOnly: boolean
  enabled?: boolean
}): { history: VersionEntry[] | null, documents: BulkGetItem[] | null } => {
  const { repository } = useRegistry()
  const { data: session } = useSession()
  const [history, setHistory] = useState<VersionEntry[] | null>(null)
  const [documents, setDocuments] = useState<BulkGetItem[] | null>(null)
  const [refetchKey, setRefetchKey] = useState(0)

  const eventTypes = useMemo(
    () => enabled && documentType ? [documentType, `${documentType}+meta`] : [],
    [enabled, documentType]
  )

  const handleRepositoryEvent = useCallback(
    (event: { uuid?: string, mainDocument?: string }) => {
      if (event.uuid === uuid || event.mainDocument === uuid) {
        setRefetchKey((k) => k + 1)
      }
    },
    [uuid]
  )

  useRepositoryEvents(eventTypes, handleRepositoryEvent)

  useEffect(() => {
    if (!enabled || !repository || !session?.accessToken) {
      return
    }

    const c1 = new AbortController()
    const c2 = new AbortController()
    const pending = {
      history: true,
      documents: false
    }

    const fetchData = async () => {
      try {
        const result = await repository.getHistory({
          accessToken: session.accessToken,
          uuid,
          abort: c1.signal
        })

        pending.history = false

        if (!result?.versions?.length) {
          setHistory(null)
          setDocuments(null)
          return
        }

        // One entry per version, newest first
        let history: VersionEntry[] = [...result.versions]
          .sort((a, b) => (a.version < b.version ? 1 : a.version > b.version ? -1 : 0))
          .map((v) => ({ version: v.version, created: v.created, status: Object.keys(v.statuses)[0] }))

        if (withStatusOnly) {
          history = history.filter((version, i) => i === 0 || version.status !== undefined)
        }

        pending.documents = true
        const documents = await repository.getDocuments({
          documents: history.map((entry) => ({ uuid, version: entry.version })),
          accessToken: session.accessToken,
          abort: c2.signal
        })

        pending.documents = false

        setHistory(history)
        setDocuments(documents?.items ?? null)
      } catch (error) {
        pending.history = false
        pending.documents = false

        if (c1.signal.aborted || c2.signal.aborted) {
          return
        }

        console.error(error)
      }
    }

    void fetchData()

    return () => {
      if (pending.history) {
        c1.abort()
      }

      if (pending.documents) {
        c2.abort()
      }
    }
  }, [enabled, uuid, repository, session?.accessToken, withStatusOnly, refetchKey])

  return { history, documents }
}
